/**
 * ContextPanelBody — "Active Context" view inside UtilityPanel.
 *
 * Tabs (in + Menu order, per Session 55 fu2): Prompts → Agents → Assets →
 * Knowledge. Default landing tab is Knowledge. Each tab renders its own
 * list. Items added to the context surface get `[Added X]` chrome (Sessions
 * 60 fu76, 62 fu).
 *
 * This is a structural port — real data plumbing (sourced from clara-core)
 * lands in a later phase. Right now it ships with mock entries so the panel
 * is testable end-to-end.
 */

import React, { useCallback, useEffect, useState } from 'react';

export type ContextTab = 'knowledge' | 'prompts' | 'agents' | 'assets';

interface MockItem {
  key: string;
  title: string;
  subtitle?: string;
  ownership?: 'personal' | 'shared';
}

const MOCK_PROMPTS: MockItem[] = [
  { key: 'p-1', title: 'Summarize this engagement', ownership: 'personal' },
  { key: 'p-2', title: 'Draft risk assessment', ownership: 'shared' },
  { key: 'p-3', title: 'Compare prior-period balances', ownership: 'shared' },
];

const MOCK_AGENTS: MockItem[] = [
  { key: 'a-1', title: 'Journal Entry Review', subtitle: 'Last run 2h ago', ownership: 'shared' },
  { key: 'a-2', title: 'Smart Automation', subtitle: 'Idle', ownership: 'personal' },
];

const MOCK_ASSETS: MockItem[] = [
  { key: 'as-1', title: 'Bank Reconciliation.xlsx' },
  { key: 'as-2', title: 'PP&E Risk Memo.docx' },
];

const MOCK_KNOWLEDGE: MockItem[] = [
  { key: 'k-1', title: 'Audit Methodology Guide' },
  { key: 'k-2', title: 'Sampling Standards' },
  { key: 'k-3', title: 'Independence Policy' },
];

const TABS: Array<{ id: ContextTab; label: string }> = [
  { id: 'prompts', label: 'Prompts' },
  { id: 'agents', label: 'Agents' },
  { id: 'assets', label: 'Assets' },
  { id: 'knowledge', label: 'Knowledge' },
];

interface ContextPanelBodyProps {
  activeTabOverride?: ContextTab | null;
}

export const ContextPanelBody = React.memo(function ContextPanelBody({
  activeTabOverride = null,
}: ContextPanelBodyProps) {
  const [activeTab, setActiveTab] = useState<ContextTab>('knowledge');
  // Track which mock items the user has "added" to context — used to flip
  // the row button from `[Add]` → `[Added X]` (Session 60/62 pattern).
  const [added, setAdded] = useState<Set<string>>(new Set());

  const toggleItem = useCallback((key: string) => {
    setAdded(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  useEffect(() => {
    if (!activeTabOverride) {
      return;
    }
    // eslint-disable-next-line react-hooks/set-state-in-effect -- external panel actions intentionally select a context tab.
    setActiveTab(activeTabOverride);
  }, [activeTabOverride]);

  const list = (() => {
    switch (activeTab) {
      case 'prompts':
        return MOCK_PROMPTS;
      case 'agents':
        return MOCK_AGENTS;
      case 'assets':
        return MOCK_ASSETS;
      case 'knowledge':
        return MOCK_KNOWLEDGE;
    }
  })();

  return (
    <div className="assets-panel__view assets-panel__view--landing">
      <div className="assets-panel__tabs" role="tablist" aria-label="Context tabs">
        {TABS.map(tab => {
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              role="tab"
              aria-selected={isActive}
              className={
                'assets-panel__tab' + (isActive ? ' assets-panel__tab--active' : '')
              }
              data-assets-tab={tab.id}
              onClick={() => setActiveTab(tab.id)}
              type="button"
            >
              {tab.label}
            </button>
          );
        })}
      </div>

      <div
        className="assets-panel__content"
        data-assets-content={activeTab}
        role="tabpanel"
      >
        {list.map(item => {
          const isAdded = added.has(item.key);
          return (
            <div
              key={item.key}
              className={
                'context-menu__row' +
                (isAdded ? ' context-menu__row--added' : '')
              }
            >
              <div className="context-menu__row-text">
                <span className="context-menu__row-title">{item.title}</span>
                {item.subtitle && (
                  <span className="context-menu__row-subtitle">{item.subtitle}</span>
                )}
              </div>
              <button
                type="button"
                className={
                  'context-menu__row-action' +
                  (isAdded ? ' context-menu__row-action--added' : '')
                }
                aria-pressed={isAdded}
                onClick={() => toggleItem(item.key)}
              >
                {isAdded ? `Added ${item.title.length > 18 ? item.title.slice(0, 18) + '…' : item.title}` : 'Add'}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
});

export default ContextPanelBody;
